import {Injectable, OnDestroy} from '@angular/core';

import { UserImageModel, UserImageResponseModel, UserService as ApiUserService, UserBaseModel } from '@core/api';
import { AuthService, UserService as AuthUserService } from '@core/auth';
import { finalize, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { TransportModel } from '@core/api/model/transportModel';
import { Observable } from 'rxjs/internal/Observable';
import { UserModel } from '@core/api/model/userModel';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { ModalPersonalDataComponent } from '@accounts/user/components/modal-personal-data/modal-personal-data.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import {Subject} from "rxjs/index";
import {timer} from "rxjs/internal/observable/timer";
import { switchMap, tap, share, retry, takeUntil } from 'rxjs/operators';
import {ModalConfirmationCodeComponent} from "@accounts/user/components/modal-confirmation-code/modal-confirmation-code.component";
import {ModalTermsComponent} from "@accounts/user/components/modal-terms/modal-terms.component";
import {RegisteredService} from "@core/auth/services/registered.service";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private imagesInitSubject = new BehaviorSubject<boolean>(false);
  private imagesSubject = new BehaviorSubject<UserImageResponseModel[]>([]);
  private transportsSubject = new ReplaySubject<TransportModel[]>(1);
  private loadingTransportsSubject = new BehaviorSubject<boolean>(false);
  private loadingFullUserDataSubject = new BehaviorSubject<boolean>(false);

  public init = this.imagesInitSubject.asObservable();
  public images = this.imagesSubject.asObservable();
  public transports = this.transportsSubject.asObservable();
  public loadingTransports = this.loadingTransportsSubject.asObservable();
  public loadingFullUserData = this.loadingFullUserDataSubject.asObservable();

  constructor(
    private registeredService: RegisteredService,
    private apiUserService: ApiUserService,
    private authUserService: AuthUserService,
    private authService: AuthService,
    private dialog: MatDialog,
    private router: Router
  ) {
    if (this.authUserService.loginIn) {
      this.pollConfirmationCodes();
    }
  }
  set user(user: UserBaseModel) {
    this.authUserService.currentUserValue = user;
  }

  get user() {
    return this.authUserService.currentUserValue;
  }

  updateUser(userModel: UserBaseModel): Observable<UserBaseModel> {
    return this.apiUserService.userPost<UserBaseModel>(userModel)
      .pipe(map(res => res.data));
  }

  getUser(): Observable<UserModel> {
    return this.apiUserService.userIdGet<UserModel>(this.user.id)
      .pipe(map(res => res.data));
  }

  uploadImage(body: UserImageModel) {
    return this.apiUserService.userIdImagePost(this.user.id, body)
      .pipe(map((res) => {
        const index = this.imagesSubject.value.findIndex(image => image.type === res.data.type);

        this.imagesSubject.next([...this.imagesSubject.value, res.data]);

        return res;
      }));
  }

  getImages() {
    return this.apiUserService.usersIdImagesGet(this.user.id)
      .pipe(finalize(() => this.imagesInitSubject.next(true)))
      .subscribe(res => {
        this.imagesSubject.next(res.data);
      });
  }

  getTransports() {
    if (!this.user || !this.user.id) {
      return;
    }

    this.loadingTransportsSubject.next(true);

    return this.apiUserService.usersIdTransportGet(this.user.id)
      .pipe(finalize(() => this.loadingTransportsSubject.next(false)))
      .subscribe(res => {
        this.transportsSubject.next(res.data);
      });
  }

  getAllTransport(): Observable<TransportModel[]> {
    return this.apiUserService.usersIdTransportGet(this.user.id)
      .pipe(map(res => res.data));
  }

  removeTransport(id: number) {
    return this.apiUserService.userIdTransportDelete(this.user.id, id);
  }

  getFileByType(type: UserImageModel.TypeEnum) {
    return this.imagesSubject.value.find(image => image.type === type);
  }

  informationDataDialog() {
    this.loadingFullUserDataSubject.next(true);

    this.getAllTransport().subscribe(([transport]) => {
      this.getUser()
        .pipe(
          finalize(() => this.loadingFullUserDataSubject.next(false))
        )
        .subscribe((user) => {
          const dialog = this.dialog.open(ModalPersonalDataComponent, {
            width: '800px',
            data: {
              user,
              transport
            }
          });

          dialog.beforeClosed().subscribe((result) => {
            if(result) {
              const url = this.router.url;

              this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
                this.router.navigate([url])
              );
            }
          });

          this.authService.logout$.subscribe(() => {
            dialog.close();
          });
        });
    });
  }

  public pollConfirmationCodes() {
    this.apiUserService.getUserConfirmationCode().subscribe(response => {
      if (!response || !response.data || !response.data.code) {
        return setTimeout(() => {
          if (this.authUserService.loginIn) {
            this.pollConfirmationCodes();
          }
        }, 1000);
      }

      const dialog = this.dialog.open(ModalConfirmationCodeComponent, {
        width: '400px',
        data: {
          code: response.data.code,
          operation_type: response.data.operation_type
        }
      });
    });
  }

  termsDialog(user: UserModel) {
    const dialog = this.dialog.open(ModalTermsComponent, {
      width: '800px',
      data: {
        user: user
      }
    });

    dialog.afterClosed().subscribe((result) => {
      if (this.registeredService.value) {
        this.informationDataDialog();
      }
    });

    this.authService.logout$.subscribe(() => {
      dialog.close();
    });
  }
}
